import { convertUnit } from 'src/utils/helpers/chartHelpers';
import { TimeTable } from 'src/utils/logs/logHelpers';
import { JobLogV2Data } from 'src/utils/logs/types';
import { JobLogV2DataPressureSort, JobPressureGraphDataSegment } from './types';

/** A collection of utility functions for converting parsed log data into a shape that can be used for drawing the graph. */
export class JobClampPressureGraphTranslator {
  // Returns a single graph segment to render the graph
  public static readonly parseDataV2 = (
    clampData: JobLogV2Data['clamp'],
    timeTable: JobLogV2Data['timeTable'],
    timeRangeMin: number,
    timeRangeMax: number,
    isMetric: boolean,
  ): JobPressureGraphDataSegment => {
    if (!clampData || clampData.length === 0 || timeTable.length === 0) return [];
    let tempData: JobLogV2DataPressureSort[] = clampData.map(d => {
      return {
        msElapsed: d.msElapsed,
        pressure: d.pressure,
        sortFirst: false,
      };
    });
    const addedItems: JobLogV2DataPressureSort[] = [];

    // Make the graph data stairstepped
    tempData.forEach((tc, i, a) => {
      const lastPressure = a[i - 1];
      if (
        // if it's not the first element
        !!lastPressure &&
        // and each x is different
        tc.msElapsed !== lastPressure.msElapsed &&
        // and each y is different
        tc.pressure !== lastPressure.pressure
      ) {
        addedItems.push({
          msElapsed: tc.msElapsed,
          pressure: lastPressure.pressure ?? 0,
          sortFirst: true, // always put added items before actual data of the same timestamp because data can go up or down
        });
      }
    });
    tempData = tempData.concat(addedItems);

    // Sort
    // 1st Priority - msElapsed
    // 2nd Priority - sortFirst
    // 3rd Priority - pressure
    tempData.sort((a, b) => {
      if (a.msElapsed < b.msElapsed) {
        return -1;
      }
      if (a.msElapsed > b.msElapsed) {
        return 1;
      }
      if (a.sortFirst && !b.sortFirst) {
        return -1;
      }
      if (!a.sortFirst && b.sortFirst) {
        return 1;
      }
      if (a.pressure < b.pressure) {
        return -1;
      }
      if (a.pressure > b.pressure) {
        return 1;
      }
      return 0;
    });

    const timeLookup = TimeTable.from(timeTable);
    // Filter only if the time range has been updated from default
    // Also filter out msElapsed of 0 to prevent date of value 0
    return tempData
      .filter(
        pressure =>
          !!pressure.msElapsed &&
          (!!timeRangeMax ? pressure.msElapsed >= timeRangeMin && pressure.msElapsed <= timeRangeMax : true),
      )
      .map((tc, i, a) => {
        const timestamp = timeLookup.getInstant(tc.msElapsed);

        return {
          x: tc.msElapsed,
          pressure: Number.parseFloat(convertUnit(tc.pressure ?? 0, 'pressure', isMetric, 3)),
          timestamp: timestamp,
        };
      });
  };
}
